import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import { useEffect, useState } from "react";
import { 
  PillBottle,
  Inbox,
  Phone,
  Settings,
  LogOut,
  Activity,
  User,
  Building2,
  MapPin,
  BadgeCheck,
  Hash,
  Mail,
  Users,
  Shield,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { LegalFooter } from "@/components/layout/legal-footer";

interface ProfileData {
  hasProfile?: boolean;
  userId?: string;
  role?: string | null;
  approvalStatus?: string | null;
  organisationName?: string | null;
  siteName?: string | null;
  teamCode?: string | null;
  jobRole?: string | null;
  staffCode?: string | null;
  workEmail?: string | null;
}

/**
 * Minimal sidebar for advicelinehub.co.uk HCPs.
 * Only shows advice line management features — no clinical tools,
 * no guidelines, no calculators. This is deliberately not RheumHub.
 */
export function AdvicelineNurseLayout({ children, title }: { children: React.ReactNode, title: string }) {
  const [location] = useLocation();
  const { signOut, user } = useClerk();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const isAdmin = useIsAdmin();

  useEffect(() => {
    document.title = `${title} | Advice Line Hub`;
    return () => { document.title = "Advice Line Hub"; };
  }, [title]);

  useEffect(() => {
    fetch("/api/user/profile-summary", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => setProfile(data))
      .catch(() => {});
  }, []);

  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";
  const displayName = [firstName, lastName].filter(Boolean).join(" ") || "Staff";
  const staffCode = profile?.staffCode ?? "—";

  const navItems = [
    { href: "/nurse", label: "Incoming Requests", icon: Inbox },
    { href: "/nurse/log-call", label: "Log a Call", icon: Phone },
    { href: "/med-tracker", label: "Medication Tracker", icon: PillBottle },
    { href: "/nurse/team", label: "Team", icon: Users },
    { href: "/nurse/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-56 bg-slate-900 text-white/75 flex flex-col fixed inset-y-0 z-20">
          {/* Brand */}
          <div className="p-5 flex items-center gap-2.5 text-white border-b border-white/10">
            <Activity className="h-5 w-5 text-emerald-400" />
            <span className="text-lg font-bold tracking-tight">Advice Line Hub</span>
          </div>

          {/* Profile Section */}
          <div className="px-4 py-3 border-b border-white/10 space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{displayName}</p>
                <p className="text-[10px] text-white/40 uppercase tracking-wide">
                  {profile?.role ?? "Nurse"}
                </p>
              </div>
            </div>

            {/* Staff Code — prominently displayed */}
            {staffCode && staffCode !== "—" && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
                <Hash className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-white/40 uppercase tracking-wide leading-none mb-0.5">Staff Code</p>
                  <p className="text-xs font-mono font-bold text-emerald-400 tracking-wider">{staffCode}</p>
                </div>
              </div>
            )}

            {/* Organisation + Location */}
            {profile?.organisationName && (
              <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{profile.organisationName}</span>
              </div>
            )}
            {profile?.siteName && (
              <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{profile.siteName}</span>
              </div>
            )}

            {/* Specialty / Job Role */}
            {profile?.jobRole && (
              <div className="flex items-center gap-1.5 text-[10px] text-white/50">
                <BadgeCheck className="h-3 w-3 shrink-0" />
                <span className="truncate capitalize">{profile.jobRole.replace(/_/g, " ")}</span>
              </div>
            )}

            {/* Shared advice line email */}
            {profile?.workEmail && (
              <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{profile.workEmail}</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="px-5 py-2 text-[10px] font-semibold uppercase tracking-wider text-white/30">
            Navigation
          </div>
          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location === item.href || location.startsWith(item.href + "/");
              return (
                <Link 
                  key={item.href} 
                  href={item.href}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                    isActive 
                      ? "bg-emerald-500/15 text-emerald-400" 
                      : "hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <item.icon className={`h-4 w-4 shrink-0 ${isActive ? "text-emerald-400" : "text-white/35"}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Admin link — only visible to admin users */}
          {isAdmin && (
            <div className="px-3 pb-2">
              <Link
                href="/admin/adviceline"
                className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors text-amber-400/70 hover:bg-amber-500/10 hover:text-amber-300"
              >
                <Shield className="h-4 w-4 shrink-0" />
                Admin Panel
              </Link>
            </div>
          )}

          {/* Sign Out */}
          <div className="p-4 border-t border-white/10">
            <button 
              onClick={() => { clearRheumHubLocalStorage(); signOut({ redirectUrl: "/" }); }}
              className="flex w-full items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium bg-white/5 hover:bg-red-500/20 hover:text-red-300 transition-colors text-white/50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 pl-56 flex flex-col min-h-screen">
          <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-8 sticky top-0 z-10">
            <h1 className="text-xl font-semibold text-slate-800">{title}</h1>
          </header>
          <div className="flex-1 p-8">
            <div className="max-w-6xl mx-auto">
              {children}
            </div>
          </div>
          <LegalFooter
            copyright="© Medantix Health Ltd · Advice Line Hub · For UK healthcare professionals only"
          />
        </main>
      </div>
    </div>
  );
}
